import { useState } from "react";
import { Box, IconButton, Typography, useTheme, Tooltip } from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import { tokens } from "../../theme";
import { useTranslation } from "react-i18next";
import SpaceDashboardOutlinedIcon from "@mui/icons-material/SpaceDashboardOutlined";
import DatasetOutlinedIcon from "@mui/icons-material/DatasetOutlined";
import PsychologyAltOutlinedIcon from "@mui/icons-material/PsychologyAltOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// Decorative SVG used as a subtle brand mark in the sidebar header background
const SidebarBrandSvg = () => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 240 240"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{
      position: "absolute",
      inset: 0,
      opacity: 0.06,
      pointerEvents: "none",
    }}
    aria-hidden="true"
  >
    <defs>
      <radialGradient id="sb-orb" cx="50%" cy="35%" r="55%">
        <stop offset="0%" stopColor="#ffffff" stopOpacity="0.9" />
        <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="120" cy="120" r="48" stroke="#ffffff" strokeWidth="1" fill="url(#sb-orb)" />
    <circle cx="120" cy="120" r="80" stroke="#ffffff" strokeWidth="0.6" />
    <circle cx="120" cy="120" r="112" stroke="#ffffff" strokeWidth="0.4" />
    {Array.from({ length: 8 }).map((_, i) => {
      const a = (i * Math.PI) / 4;
      return (
        <circle
          key={i}
          cx={120 + Math.cos(a) * 80}
          cy={120 + Math.sin(a) * 80}
          r="2.5"
          fill="#ffffff"
        />
      );
    })}
  </svg>
);

const Sidebar = ({ items, bottomItems }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  const menuItems = [
    { title: t('navigation.dashboard'),            path: "/dashboard", icon: <SpaceDashboardOutlinedIcon />, badge: null },
    { title: t('navigation.datasets'),             path: "/datasets",  icon: <DatasetOutlinedIcon />,        badge: null },
    { title: t('navigation.sites', 'Client Sites'),path: "/sites",     icon: <AccountTreeOutlinedIcon />,    badge: null },
    { title: t('navigation.models'),               path: "/models",    icon: <PsychologyAltOutlinedIcon />,  badge: null },
    { title: t('navigation.configurations'),       path: "/configs",   icon: <TuneOutlinedIcon />,           badge: null },
    { title: t('navigation.execute'),              path: "/execute",   icon: <RocketLaunchOutlinedIcon />,   badge: null },
  ];

  const bottomMenuItems = [];

  const isActiveRoute = (path) => {
    if (path === "/") {
      return location.pathname === "/" || location.pathname === "/dashboard";
    }
    if (path === "/documentation") {
      return location.pathname === "/documentation";
    }
    return location.pathname.startsWith(path);
  };

  const collapsedWidth = 68;
  const expandedWidth = 232;

  return (
    <Box
      sx={{
        width: isCollapsed ? collapsedWidth : expandedWidth,
        minWidth: isCollapsed ? collapsedWidth : expandedWidth,
        maxWidth: isCollapsed ? collapsedWidth : expandedWidth,
        flexShrink: 0,
        minHeight: "100vh",
        background: `linear-gradient(180deg, #1B365D 0%, #132643 60%, #0b1629 100%)`,
        transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        overflow: "visible",
        boxShadow: "1px 0 0 rgba(255,255,255,0.04), 4px 0 24px rgba(11, 22, 41, 0.18)",
        zIndex: 1200,
      }}
    >
      {/* SVG decorative accent — subtle, premium feel */}
      <Box
        sx={{
          position: "absolute",
          top: 60,
          left: -40,
          right: -40,
          height: 280,
          zIndex: 0,
          pointerEvents: "none",
        }}
      >
        <SidebarBrandSvg />
      </Box>

      {/* Header */}
      <Box
        sx={{
          px: isCollapsed ? 1.5 : 2.5,
          height: 54,
          display: "flex",
          alignItems: "center",
          justifyContent: isCollapsed ? "center" : "flex-start",
          position: "relative",
          zIndex: 1,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {!isCollapsed ? (
          <Box display="flex" alignItems="center" gap={1.25}>
            <Box
              component="img"
              alt="FederateX"
              src="/federatex-mark.svg"
              sx={{ width: 26, height: 26, objectFit: "contain", borderRadius: "6px" }}
            />
            <Typography
              sx={{
                color: "#ffffff",
                fontWeight: 800,
                letterSpacing: "0.04em",
                fontSize: "0.9375rem",
              }}
            >
              FEDERATE<Box component="span" sx={{ color: "#36CE7B" }}>X</Box>
            </Typography>
          </Box>
        ) : (
          <Box
            component="img"
            alt="FederateX"
            src="/federatex-mark.svg"
            sx={{ width: 26, height: 26, objectFit: "contain", cursor: "pointer", borderRadius: "6px" }}
            onClick={() => setIsCollapsed(false)}
          />
        )}

        <IconButton
          onClick={() => setIsCollapsed(!isCollapsed)}
          size="small"
          sx={{
            position: "absolute",
            right: -11,
            top: 15,
            width: 22,
            height: 22,
            backgroundColor: colors.primary[500],
            color: "#ffffff",
            border: "1px solid rgba(255,255,255,0.18)",
            boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
            zIndex: 100,
            "&:hover": {
              backgroundColor: colors.primary[400],
            },
          }}
        >
          {isCollapsed ? <ChevronRightIcon sx={{ fontSize: 14 }} /> : <ChevronLeftIcon sx={{ fontSize: 14 }} />}
        </IconButton>
      </Box>

      {/* Nav section label */}
      {!isCollapsed && (
        <Typography
          sx={{
            color: "rgba(255,255,255,0.45)",
            fontSize: "0.625rem",
            fontWeight: 600,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            px: 2.5,
            pt: 2,
            pb: 1,
            position: "relative",
            zIndex: 1,
          }}
        >
          Workspace
        </Typography>
      )}
      {isCollapsed && <Box sx={{ height: 16 }} />}

      {/* Main Navigation */}
      <Box sx={{ flex: 1, position: "relative", zIndex: 1, overflowY: "auto", px: isCollapsed ? 1 : 1.25 }}>
        {(items || menuItems).map((item) => {
          const active = isActiveRoute(item.path);
          return (
            <Box key={item.title}>
              <Tooltip title={isCollapsed && !item.children ? item.title : ""} placement="right" arrow>
                <Box
                  component={item.children ? "div" : Link}
                  to={item.children ? undefined : item.path}
                  onClick={() => {
                    if (item.children && isCollapsed) setIsCollapsed(false);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: active ? "#ffffff" : "rgba(255,255,255,0.72)",
                    backgroundColor: active ? "rgba(0,135,180,0.18)" : "transparent",
                    px: isCollapsed ? 1.25 : 1.5,
                    py: 1.1,
                    my: 0.25,
                    textDecoration: "none",
                    transition: "background-color 0.15s ease, color 0.15s ease",
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: "6px",
                    cursor: "pointer",
                    justifyContent: isCollapsed ? "center" : "flex-start",
                    "&:hover": {
                      backgroundColor: active ? "rgba(0,135,180,0.22)" : "rgba(255,255,255,0.06)",
                      color: "#ffffff",
                    },
                    "&::before": active
                      ? {
                          content: '""',
                          position: "absolute",
                          left: 0,
                          top: "20%",
                          bottom: "20%",
                          width: 3,
                          borderRadius: "0 2px 2px 0",
                          backgroundColor: "#0087B4",
                          boxShadow: "0 0 8px rgba(0,135,180,0.6)",
                        }
                      : {},
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.1rem",
                      color: active ? "#7fd1ec" : "rgba(255,255,255,0.78)",
                    }}
                  >
                    {item.icon}
                  </Box>
                  {!isCollapsed && (
                    <Typography
                      sx={{
                        ml: 1.5,
                        fontWeight: active ? 600 : 500,
                        fontSize: "0.8125rem",
                        letterSpacing: "0.01em",
                        color: active ? "#ffffff" : "rgba(255,255,255,0.78)",
                      }}
                    >
                      {item.title}
                    </Typography>
                  )}
                </Box>
              </Tooltip>

              {!isCollapsed && item.children && (
                <Box sx={{ ml: 3, display: "flex", flexDirection: "column", gap: 0.25, mb: 0.5 }}>
                  {item.children.map((child) => {
                    const childActive = isActiveRoute(child.path);
                    return (
                      <Box
                        key={child.title}
                        component={Link}
                        to={child.path}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          color: childActive ? "#ffffff" : "rgba(255,255,255,0.55)",
                          py: 0.75,
                          pl: 1.5,
                          textDecoration: "none",
                          borderRadius: "5px",
                          fontSize: "0.75rem",
                          "&:hover": {
                            color: "#ffffff",
                            backgroundColor: "rgba(255,255,255,0.05)",
                          },
                        }}
                      >
                        <Typography sx={{ fontSize: "0.75rem" }}>{child.title}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Bottom Navigation */}
      {(bottomItems || bottomMenuItems).length > 0 && (
        <Box sx={{ py: 1, px: isCollapsed ? 1 : 1.25, position: "relative", zIndex: 1 }}>
          {(bottomItems || bottomMenuItems).map((item) => {
            const active = isActiveRoute(item.path);
            return (
              <Tooltip key={item.title} title={isCollapsed ? item.title : ""} placement="right" arrow>
                <Box
                  component={Link}
                  to={item.path}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    color: active ? "#ffffff" : "rgba(255,255,255,0.72)",
                    backgroundColor: active ? "rgba(0,135,180,0.18)" : "transparent",
                    px: isCollapsed ? 1.25 : 1.5,
                    py: 1.1,
                    my: 0.25,
                    textDecoration: "none",
                    borderRadius: "6px",
                    transition: "background-color 0.15s ease",
                    "&:hover": {
                      backgroundColor: active ? "rgba(0,135,180,0.22)" : "rgba(255,255,255,0.06)",
                      color: "#ffffff",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.1rem" }}>
                    {item.icon}
                  </Box>
                  {!isCollapsed && (
                    <Typography sx={{ ml: 1.5, fontSize: "0.8125rem", fontWeight: active ? 600 : 500 }}>
                      {item.title}
                    </Typography>
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      )}

      {/* Footer */}
      <Box
        sx={{
          px: isCollapsed ? 1.5 : 2.5,
          py: 2,
          position: "relative",
          zIndex: 1,
          borderTop: "1px solid rgba(255,255,255,0.06)",
          display: "flex",
          flexDirection: isCollapsed ? "column" : "column",
          alignItems: isCollapsed ? "center" : "flex-start",
          gap: 0.75,
        }}
      >
        {isCollapsed ? (
          <Tooltip title={`v1.0.1`} placement="right" arrow>
            <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.625rem", letterSpacing: "0.02em" }}>
              v1.0.1
            </Typography>
          </Tooltip>
        ) : (
          <Typography
            sx={{
              color: "rgba(255,255,255,0.45)",
              fontSize: "0.6875rem",
              letterSpacing: "0.02em",
            }}
          >
            Version 1.0.1
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default Sidebar;
